/* eslint-disable max-len */
import {
  Checkbox,
  formatPercent,
  investmentFrequencies,
  liquidityNeeds,
  normalizePercent,
  required,
  riskTolerances,
  RadioButtonGroup,
  yearsOfExperience,
} from 'entoro-shared';
import React from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import RequestInformationButton from '../RealexRequestInformationButton';
import { Button } from 'material-ui';
import { withStyles } from 'material-ui';
import { FormControl, FormHelperText, FormLabel } from 'material-ui/Form';
import MaterialTextField from 'material-ui/TextField';

const styles = (theme) => ({
  textFieldRoot: {
    padding: 0,
    'label + &': {
      marginTop: theme.spacing.unit * 3,
    },
  },
  textFieldInput: {
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 12px',
    width: 'calc(100% - 24px)',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  selectPadding: {
    padding: ' 3px',
  },
  InputLabelStyle: {
    top: '-10px',
    left: ' 10px',
  },

  listItemTextRoot: {
    fontSize: '16px',
    '& .MuiTypography-subheading-53': {
      fontSize: '15px',
    },
  },

  toolbarRoots: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  dialogcontentRoot: {
    padding: '20px 0px 0px 24px',
  },
  formControlMargin: {
    marginTop: 30,
    marginBottom: '-20px',
  },
  labelStyle: {
    minHeight: '32px',
    marginBottom: '20px',
    fontFamily: 'Heebo, sans-serif',
    fontSize: '26px',
    lineHeight: '32px',
    fontWeight: '400',
    letterSpacing: '0.41px',
  },
});

const TextField = ({
  input,
  meta: { touched, error },
  styleInput,
  label,
  ...custom
}) => {
  return (
    <FormControl
      classes={{ root: styleInput.formControlMargin }}
      fullWidth
      error={Boolean(touched && error)}
    >
      <FormLabel classes={{ root: styleInput.labelStyle }}>{label}</FormLabel>
      <MaterialTextField
        {...input}
        error={Boolean(touched && error)}
        fullWidth
        {...custom}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: styleInput.textFieldRoot,
            input: styleInput.textFieldInput,
          },
        }}
      />
      {touched && error ? <FormHelperText>{error}</FormHelperText> : null}
    </FormControl>
  );
};

const RealexSuitabilty = reduxForm({
  form: 'realexSuitabiltiesForm',
})((props) => {
  const {
    handleSubmit,
    submitting,
    submitSuitability,
    issuerCompany,
    hasSubmitted,
    liquidityNeedsValue,
    riskToleranceValue,
    investmentAllocationValue,
    offerId,
    userId,
    classes,
  } = props;

  const checkboxLabels = {
    investmentDecisionConsultProfessional: `I understand early-stage businesses are very risky. I can afford to lose the entirety of all investments I make in ${issuerCompany}`,
    investmentDecisionConsultSelf: `I understand that my investment in ${issuerCompany} is not transferrable for 12 months, and beyond that,
    privately issued securities of this type are not easily re-sold. The lack of mature secondary markets or exchanges
    for these securities means that even if I do find a buyer I may not be able to obtain a fair price, and that it can
    take years to realize a return`,
    investmentDecisionConsultFamilyFriends: `I understand that spreading the investment I plan to make in ${issuerCompany} among multiple deals would
    diversify my risk`,
    investmentSelectionsConfirmed: `I understand that OfferBoard does not offer investment advice. I am sufficiently financially sophisticated and
    educated to evaluate companies based on the materials provided on the ${issuerCompany} funding portal
    and to make independent investment decisions`,
    investmentSelectionsRealexPrecentage: `I understand that OfferBoard and or its affiliated Broker-Dealer Entoro Securities, LLC will receive a variable
    share of Realex's commission for this offering
    `,
    investmentSelectionsRelationshipConfirmed: `I understand that OfferBoard and ${issuerCompany} may not maintain a relationship beyond the conclusion
    of this offering
    `,
  };
  const validation = (value) => {
    if (liquidityNeedsValue === 'VERY_IMPORTANT') {
      return null;
    } else if (liquidityNeedsValue !== 'VERY_IMPORTANT') {
      return value;
    }
    if (riskToleranceValue === 'CONSERVATIVE') {
      return null;
    } else if (riskToleranceValue !== 'CONSERVATIVE') {
      return value;
    }

    return false;
  };
  const riskvalidation = (value) => {
    if (riskToleranceValue === 'CONSERVATIVE') {
      return null;
    } else if (riskToleranceValue !== 'CONSERVATIVE') {
      return value;
    }
  };
  return (
    <form onSubmit={handleSubmit(submitSuitability)}>
      <div className='form_margin w-clearfix w-form'>
        {(liquidityNeedsValue === 'VERY_IMPORTANT' ||
          riskToleranceValue === 'CONSERVATIVE') && (
          <RequestInformationButton
            offerId={offerId}
            realexRequestform={true}
            userId={userId}
          />
        )}
        {liquidityNeedsValue === 'IMPORTANT' &&
          investmentAllocationValue > 0.4 && (
            <RequestInformationButton
              offerId={offerId}
              realexRequestform={true}
              userId={userId}
            />
          )}
        <div className='content-spacer'>
          <div className='column-fields w-row'>
            <div className='column-4 w-col w-col-6'>
              <Field
                className='checkbox-field w-checkbox'
                component={RadioButtonGroup}
                validate={[required, validation]}
                format={validation}
                name='liquidityNeeds'
                label='Liquidity Needs'
                helperText='The ability to quickly and easily convert to cash all or a portion of this investment without experiencing significant loss in value from, for example, the lack of a ready market, or incurring significant costs or penalties is:'
                options={liquidityNeeds.options}
                required
              />
            </div>
            <div className='column-5 w-col w-col-6'>
              <Field
                className='checkbox-field w-checkbox'
                component={RadioButtonGroup}
                disabled={
                  !liquidityNeedsValue ||
                  liquidityNeedsValue === 'VERY_IMPORTANT'
                }
                format={riskvalidation}
                validate={[required]}
                name='riskTolerance'
                label='Risk Tolerance'
                helperText='Please select the degree of risk you (and any co-Subscribers, if applicable) are willing to take with this investment.'
                options={riskTolerances.options}
                required
              />
            </div>
          </div>
        </div>
        <div className='column-fields w-row'>
          <div className='column-4 w-col w-col-6'>
            <Field
              className='checkbox-field w-checkbox'
              component={RadioButtonGroup}
              validate={[required]}
              disabled={
                !riskToleranceValue || riskToleranceValue === 'CONSERVATIVE'
              }
              name='yearsOfExperience'
              label='Years of Experience'
              helperText='How many years’ experience do you have investing in private placements and/or alternative investments (e.g., structured products, hedge funds)?'
              options={yearsOfExperience.options}
              required
            />
          </div>
          <div className='column-5 w-col w-col-6'>
            <Field
              className='checkbox-field w-checkbox'
              component={RadioButtonGroup}
              validate={[required]}
              disabled={
                !riskToleranceValue || riskToleranceValue === 'CONSERVATIVE'
              }
              name='investmentFrequency'
              label='Investment Frequency'
              helperText='How many such investments do you make per year?'
              options={investmentFrequencies.options}
              required
            />
          </div>
        </div>
        <div className='spacer'>
          <Field
            className='checkbox-field w-checkbox'
            component={TextField}
            validate={[required]}
            disabled={
              !riskToleranceValue || riskToleranceValue === 'CONSERVATIVE'
            }
            styleInput={classes}
            name='investmentAllocation'
            label='Investment Allocation (%)'
            helperText='Please estimate the percentage of your total portfolio (excluding personal residence) that would be allocated to private placements as a result of this transaction.'
            required
            normalize={normalizePercent}
            format={formatPercent}
          />
        </div>
        <div className='content-spacer'>
          <div className='_26-black'>
            Prior to making an investment decision (check all that apply):
          </div>
          <div className='column-fields w-row'>
            <div className='w-col w-col-7'>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  validate={[required]}
                  name='investmentDecisionConsultProfessional'
                  label={checkboxLabels.investmentDecisionConsultProfessional}
                />
              </div>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  disabled={hasSubmitted}
                  validate={[required]}
                  name='investmentDecisionConsultSelf'
                  label={checkboxLabels.investmentDecisionConsultSelf}
                />
              </div>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  disabled={hasSubmitted}
                  validate={[required]}
                  name='investmentDecisionConsultFamilyFriends'
                  label={checkboxLabels.investmentDecisionConsultFamilyFriends}
                />
              </div>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  disabled={hasSubmitted}
                  validate={[required]}
                  name='investmentSelectionsConfirmed'
                  label={checkboxLabels.investmentSelectionsConfirmed}
                />
              </div>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  disabled={hasSubmitted}
                  validate={[required]}
                  name='investmentSelectionsRealexPrecentage'
                  label={checkboxLabels.investmentSelectionsRealexPrecentage}
                />
              </div>
              <div className='checkbox-field w-checkbox'>
                <Field
                  component={Checkbox}
                  disabled={hasSubmitted}
                  validate={[required]}
                  name='investmentSelectionsRelationshipConfirmed'
                  label={
                    checkboxLabels.investmentSelectionsRelationshipConfirmed
                  }
                />
              </div>
            </div>
            <div className='w-col w-col-5' />
          </div>
        </div>

        <div className='grey-regular notice'>
          Notice regarding our Customer Identification Program (CIP). To help
          the government fight the funding of terrorism and money laundering
          activities, federal law requires financial institutions to obtain,
          verify, and record information that identifies each person who opens
          an account (for purposes of CIP, an account is a formal relationship
          with a broker-dealer established to effect transactions in
          securities).
          <br />‍<br />
          When you open an account, we are required to collect information such
          as your name, date of birth, address, Identification Number (U.S.
          Citizen: taxpayer identification number (social security number or
          employer identification number); Non-U.S. Citizen: taxpayer
          identification number, passport number, and country of issuance, alien
          identification card number, or government issued identification
          showing nationality, residence, and a photograph of you.) You may also
          need to show your driver&apos;s license or other identifying
          documents.
          <br />‍<br />A corporation, partnership, trust or other legal entity
          may need to provide other information, such as its principal place of
          business, local office, employer identification number, certified
          articles of incorporation, government-issued business license, a
          partnership agreement or a trust agreement.
          <br />‍<br />
          U.S. Department of the Treasury, Securities and Exchange Commission,
          and FINRA already require you to provide most of this information.
          These rules also may require you to provide additional information,
          such as your net worth, annual income, occupation, employment
          information, investment experience and objectives, and risk tolerance.
          <br />‍<br />
          The information collected about you may be shared with third-party
          vendors and service providers who perform services for us, such as
          identification verification, fraud prevention, advertising, mailing
          services, tax and accounting services, contest fulfillment, web
          hosting, and analytics services. 
          <br />‍<br />
          If you have any questions, please contact us at {' '}
          <a href='mailto:support@offerboard.com'>support@offerboard.com</a>.
        </div>
        <div className='full-line bottom' />

        <Button
          type='submit'
          style={{ color: '#fff' }}
          disabled={
            !liquidityNeedsValue ||
            (liquidityNeedsValue === 'IMPORTANT' &&
              investmentAllocationValue > 0.4)
          }
          className={`button-2 w-button ${
            !liquidityNeedsValue ||
            (liquidityNeedsValue === 'IMPORTANT' &&
              investmentAllocationValue > 0.4)
              ? 'btn-disabled'
              : ''
          }`}
        >
          Save & Continue
        </Button>
      </div>
    </form>
  );
});

const mapStateToProps = (state) => {
  const selector = formValueSelector('realexSuitabiltiesForm');
  return {
    liquidityNeedsValue: selector(state, 'liquidityNeeds'),
    riskToleranceValue: selector(state, 'riskTolerance'),
    investmentAllocationValue: selector(state, 'investmentAllocation'),
  };
};

export default connect(mapStateToProps)(withStyles(styles)(RealexSuitabilty));



// WEBPACK FOOTER //
// ./src/components/RealexOffer/register/RealexSuitabilty.js